import FingerprintJS, { type Agent } from "@fingerprintjs/fingerprintjs";
import { getCookie, setCookie } from "react-use-cookie";

const FINGERPRINT_COOKIE = "__consensus_fingerprint";
const FINGERPRINT_VERSION = 1;

const isBrowser = typeof window !== "undefined";

interface FingerprintData {
  fingerprint: string;
  version: number;
  timestamp: number;
}

let fpPromise: Promise<Agent> | null = null;

const getFingerprintAgent = async (): Promise<Agent> => {
  if (!fpPromise) {
    fpPromise = FingerprintJS.load();
  }
  return fpPromise;
};

// For testing only - reset the singleton
export const __resetFingerprintAgent = () => {
  fpPromise = null;
};

const parseFingerprintCookie = (
  cookieValue: string | null
): FingerprintData | null => {
  if (!cookieValue) return null;

  try {
    const data = JSON.parse(cookieValue) as FingerprintData;
    if (data.version !== FINGERPRINT_VERSION) {
      return null;
    }
    return data;
  } catch (error) {
    return null;
  }
};

const storeFingerprintCookie = (fingerprint: string): void => {
  const data: FingerprintData = {
    fingerprint,
    version: FINGERPRINT_VERSION,
    timestamp: Date.now(),
  };
  setCookie(FINGERPRINT_COOKIE, JSON.stringify(data));
};

export const generateFingerprint = async (): Promise<string> => {
  if (!isBrowser) {
    throw new Error("Fingerprinting is only available in browser environment");
  }

  const fp = await getFingerprintAgent();
  const result = await fp.get();
  return result.visitorId;
};

export const getFingerprint = async (): Promise<string> => {
  if (!isBrowser) {
    throw new Error("Fingerprinting is only available in browser environment");
  }

  const cookieValue = getCookie(FINGERPRINT_COOKIE);
  const existingFingerprintData = parseFingerprintCookie(cookieValue);

  if (existingFingerprintData) {
    return existingFingerprintData.fingerprint;
  }

  const newFingerprint = await generateFingerprint();
  storeFingerprintCookie(newFingerprint);
  return newFingerprint;
};
